import type { Terminal } from "@xterm/xterm";
import { extractPathCandidates } from "./fileRefDetect";
import { resolveFileRef, type ResolveCtx, type ResolvedRef } from "./fileRefResolve";

export interface FileLinkOptions {
  // A GETTER, not a snapshot: provideLinks runs lazily on hover, and the
  // provider is registered once for the terminal's lifetime (the mount effect
  // only re-runs on terminal.id). Reading the ctx fresh per hover lets a
  // project/root switch take effect without re-registering — otherwise hovers
  // resolve against the old root and open the previous project's file (Codex C2).
  getCtx: () => ResolveCtx;
  onOpenFileRef: (ref: ResolvedRef) => void;
}

/**
 * Register a file-path link provider on an xterm terminal. The provider owns
 * the hover affordance (underline) and tracks the currently hovered resolved
 * ref; a double-click on the terminal element opens it. Returns a disposer.
 * Resolution runs on hover (per provideLinks call), never per output line.
 */
export function registerFileLinkProvider(term: Terminal, opts: FileLinkOptions): () => void {
  let hovered: ResolvedRef | null = null;

  const providerDisposable = term.registerLinkProvider({
    provideLinks(lineNumber: number, callback: (links: any[] | undefined) => void) {
      const line = term.buffer.active.getLine(lineNumber - 1);
      const text = line?.translateToString(true) ?? "";
      const candidates = extractPathCandidates(text);
      if (candidates.length === 0) {
        callback([]);
        return;
      }
      // Snapshot the current ctx once for this hover so every candidate on the
      // line resolves under the same (latest) project root.
      const ctx = opts.getCtx();
      void Promise.all(candidates.map((c) => resolveFileRef(c, ctx).then((r) => ({ c, r })))).then(
        (pairs) => {
          const links: any[] = [];
          for (const { c, r } of pairs) {
            if (!r) continue;
            const range = {
              start: { x: c.start + 1, y: lineNumber },
              // c.end is the 0-based EXCLUSIVE end == correct 1-based INCLUSIVE
              // end for xterm's IBufferRange (matches web-links addon).
              end: { x: c.end, y: lineNumber },
            };
            links.push({
              range,
              text: c.raw,
              decorations: { underline: true, pointerCursor: true },
              activate: (_event: MouseEvent, _text: string) => opts.onOpenFileRef(r),
              hover: (_event: MouseEvent, _text: string) => {
                hovered = r;
              },
              leave: (_event: MouseEvent, _text: string) => {
                if (hovered === r) hovered = null;
              },
            });
          }
          callback(links);
        }
      );
    },
  });

  function onDblClick(e: MouseEvent) {
    if (!hovered) return;
    e.preventDefault();
    opts.onOpenFileRef(hovered);
  }
  const el = term.element;
  el?.addEventListener("dblclick", onDblClick);

  return () => {
    providerDisposable.dispose();
    el?.removeEventListener("dblclick", onDblClick);
  };
}
